import { noop, useAudio } from "@aircall/tractor";
import { useTrack } from "@/hooks/useTrack/useTrack";
import {
  Method,
  RecordingPlayedPayload,
  VoicemailPlayedPayload,
} from "@/hooks/useTrack/useTrack.decl";
import { useTrackHelpers } from "@/hooks/useTrackHelpers/useTrackHelpers";
import { useBaseStore } from "@/store/store";
import { useCall } from "../useCall/useCall";
import { useGetLine } from "../useGetLine/useGetLine";

type OnUtteranceBarClickCallbackArgs = {
  utteranceSpeaker: RecordingPlayedPayload["utterance_speaker"];
  sentimentAtPlay: RecordingPlayedPayload["sentiment_at_play"];
};

type UseTrackPlayingReturn<M extends Method> = M extends "utterance_bar"
  ? (args: OnUtteranceBarClickCallbackArgs) => void
  : () => void;

type OnClickArgs<M extends Method> = {
  track: ReturnType<typeof useTrack>["track"];
  method: M;
  isVoicemail: boolean;
  commonVoicemailPayload: Omit<VoicemailPlayedPayload, "method">;
  commonRecordingPayload: Omit<RecordingPlayedPayload, "method">;
};

function onPlayButtonClick({
  method,
  isVoicemail,
  track,
  commonVoicemailPayload,
  commonRecordingPayload,
}: OnClickArgs<"play_button">) {
  return () => {
    if (isVoicemail) {
      track("voicemail_played", {
        ...commonVoicemailPayload,
        method,
      });
    } else {
      track("recording_played", {
        ...commonRecordingPayload,
        method,
      });
    }
  };
}

function onAudioBarClick({
  method,
  isVoicemail,
  track,
  commonVoicemailPayload,
  commonRecordingPayload,
}: OnClickArgs<"audio_bar">) {
  return () => {
    if (isVoicemail) {
      track("voicemail_played", {
        ...commonVoicemailPayload,
        method,
      });
    } else {
      track("recording_played", {
        ...commonRecordingPayload,
        method,
      });
    }
  };
}

function onTranscriptClick({
  method,
  isVoicemail,
  track,
  commonVoicemailPayload,
  commonRecordingPayload,
}: OnClickArgs<"transcript">) {
  return () => {
    if (isVoicemail) {
      track("voicemail_played", {
        ...commonVoicemailPayload,
        method,
      });
    } else {
      track("recording_played", {
        ...commonRecordingPayload,
        method,
      });
    }
  };
}

function onUtteranceBarClick({
  method,
  track,
  commonRecordingPayload,
}: Omit<
  OnClickArgs<"utterance_bar">,
  "isVoicemail" | "commonVoicemailPayload"
>) {
  return ({
    utteranceSpeaker,
    sentimentAtPlay,
  }: OnUtteranceBarClickCallbackArgs) => {
    // Cannot be a voicemail here.
    track("recording_played", {
      ...commonRecordingPayload,
      method,
      utterance_speaker: utteranceSpeaker,
      sentiment_at_play: sentimentAtPlay,
    });
  };
}

// Asset page tracking
export function useTrackPlaying<M extends Method>(
  method: M
): UseTrackPlayingReturn<M> {
  const { track } = useTrack();
  const { speed } = useAudio();
  const { call } = useCall();
  const { lineId } = useGetLine();

  const transcriptionSettings = useBaseStore(
    (store) => store.transcriptionSettings
  );

  const {
    transcription_displayed,
    summary_displayed,
    key_topics_displayed,
    talk_ratio_displayed,
    reason_for_no_transcript_displayed,
    sentiment_displayed,
  } = useTrackHelpers();

  const isVoicemail = call?.type === "voicemail";

  const commonVoicemailPayload: Omit<VoicemailPlayedPayload, "method"> = {
    call_direction: call?.direction,
    call_uuid: call?.uuid,
    transcription_displayed,
    speed_value: String(speed),
    number_id: lineId,
    location: "assets_page",
    key_topics_displayed,
    summary_displayed,
    talk_ratio_displayed,
    reason_for_no_transcript_displayed,
    transcription_language: transcriptionSettings?.lineLanguage,
  };

  const commonRecordingPayload: Omit<RecordingPlayedPayload, "method"> = {
    sentiment_displayed,
    utterance_speaker: null,
    sentiment_at_play: null,
    call_direction: call?.direction,
    call_uuid: call?.uuid,
    transcription_displayed,
    speed_value: String(speed),
    number_id: lineId,
    location: "assets_page",
    key_topics_displayed,
    summary_displayed,
    talk_ratio_displayed,
    reason_for_no_transcript_displayed,
    transcription_language: transcriptionSettings?.lineLanguage,
  };

  switch (method) {
    case "play_button":
      return onPlayButtonClick({
        method,
        isVoicemail,
        commonVoicemailPayload,
        commonRecordingPayload,
        track,
      });
    case "audio_bar":
      return onAudioBarClick({
        method,
        isVoicemail,
        commonVoicemailPayload,
        commonRecordingPayload,
        track,
      });
    case "transcript":
      return onTranscriptClick({
        method,
        isVoicemail,
        commonVoicemailPayload,
        commonRecordingPayload,
        track,
      });
    case "utterance_bar":
      // Not sure why TypeScript does not understand without the assertion. The assert here is not really valid, but at least we get the inference.
      return onUtteranceBarClick({
        method,
        commonRecordingPayload,
        track,
      }) as UseTrackPlayingReturn<M>;
    default:
      return noop;
  }
}
